import { useState } from 'react'
import { subDays } from 'date-fns'
import {
  keepPreviousData,
  useQuery,
  useSuspenseQuery,
} from '@tanstack/react-query'
import { Link, createFileRoute } from '@tanstack/react-router'
import { Building2, ChevronRight, CreditCard, Loader2 } from 'lucide-react'
import { expandUsageKinds, formatCredits } from '@/lib/credits'
import {
  type DailyCredits,
  type DailyUsers,
  type UsageTypeCount,
  adminBillingAccountsQueryOptions,
  adminDashboardMetricsQueryOptions,
  adminOrganizationsQueryOptions,
} from '@/lib/queries/admin'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { CategoryBarChart } from '@/components/charts/category-bar-chart'
import { MetricCard } from '@/components/charts/metric-card'
import { TimeSeriesChart } from '@/components/charts/time-series-chart'
import { DateRangePicker } from '@/components/date-range-picker'
import { LumooSpinner } from '@/components/lumoo-spinner'

type StrictDateRange = { from: Date; to: Date }

export const Route = createFileRoute('/_authenticated/admin/')({
  // Awaited so the initial load goes through the router's pending state
  // (PageSpinner) like every other admin route. Metrics use a non-suspending
  // useQuery in the component only so date-range changes keep the previous
  // charts on screen instead of blanking the page.
  loader: async ({ context }) => {
    const defaultRange = { from: subDays(new Date(), 29), to: new Date() }
    await Promise.all([
      context.queryClient.query({
        ...adminOrganizationsQueryOptions(),
        staleTime: 'static',
      }),
      context.queryClient.query({
        ...adminBillingAccountsQueryOptions(),
        staleTime: 'static',
      }),
      context.queryClient.query({
        ...adminDashboardMetricsQueryOptions(defaultRange),
        staleTime: 'static',
      }),
    ])
  },
  component: AdminDashboard,
})

function AdminDashboard() {
  const [dateRange, setDateRange] = useState<StrictDateRange>({
    from: subDays(new Date(), 29),
    to: new Date(),
  })

  const { data: organizations } = useSuspenseQuery(
    adminOrganizationsQueryOptions()
  )
  const { data: billingAccounts } = useSuspenseQuery(
    adminBillingAccountsQueryOptions()
  )

  const { data: metrics, isFetching } = useQuery({
    ...adminDashboardMetricsQueryOptions(dateRange),
    placeholderData: keepPreviousData,
  })

  // Only reachable if the cache misses the loader's key (e.g. a mount that
  // straddles midnight). Full-width so it centers inside the layout's flex row.
  if (!metrics) {
    return (
      <div className='flex h-96 w-full items-center justify-center'>
        <LumooSpinner size={100} />
      </div>
    )
  }

  const dailyCredits =
    (metrics.daily_credits as unknown as DailyCredits[]) || []
  const creditsByType =
    (metrics.credits_by_type as unknown as UsageTypeCount[]) || []
  const dailyUsers = (metrics.daily_users as unknown as DailyUsers[]) || []

  const formatDate = (date: string) =>
    new Date(date).toLocaleDateString('en-150', {
      month: 'short',
      day: 'numeric',
    })

  const totalUsersData = dailyUsers.map((d) => ({
    date: formatDate(d.date),
    total_users: d.total_users,
  }))
  const mauData = dailyUsers.map((d) => ({
    date: formatDate(d.date),
    mau: d.mau,
  }))
  const creditsData = dailyCredits.map((d) => ({
    date: formatDate(d.date),
    credits: d.credits,
  }))
  // One bar per generation kind — the axis an admin actually wants to see.
  const typesData = expandUsageKinds(creditsByType)
  const creditTooltip = (v: number) =>
    `${formatCredits(v)} ${v === 1 ? 'credit' : 'credits'}`

  return (
    <div className='w-full space-y-6'>
      <div className='flex items-end justify-between gap-2'>
        <div>
          <h2 className='font-heading text-xl'>Dashboard</h2>
          <p className='text-muted-foreground text-sm'>
            Platform-wide activity and adoption
          </p>
        </div>
        <div className='flex items-center gap-2'>
          {isFetching && (
            <Loader2 className='text-muted-foreground h-4 w-4 animate-spin' />
          )}
          <DateRangePicker
            value={dateRange}
            onChange={(range) => {
              if (range?.from && range?.to) {
                setDateRange({ from: range.from, to: range.to })
              }
            }}
          />
        </div>
      </div>

      {/* User Charts */}
      <div className='grid gap-4 lg:grid-cols-2'>
        <MetricCard
          title='Total users'
          metric={`${metrics.total_users.toLocaleString()} registered`}
          description='Cumulative user signups for selected period'
        >
          <TimeSeriesChart
            data={totalUsersData}
            dataKey='total_users'
            type='line'
            tooltipLabel='Total'
            tooltipFormatter={(v) => `${v.toLocaleString()} users`}
          />
        </MetricCard>

        <MetricCard
          title='Monthly active users'
          metric={`${metrics.mau.toLocaleString()} active`}
          description='Rolling 30-day users who spent credits.'
        >
          <TimeSeriesChart
            data={mauData}
            dataKey='mau'
            type='area'
            tooltipLabel='MAU'
            tooltipFormatter={(v) => `${v.toLocaleString()} users`}
          />
        </MetricCard>
      </div>

      {/* Credit Charts */}
      <div className='grid gap-4 lg:grid-cols-2'>
        <MetricCard
          title='Credits over time'
          metric={`${formatCredits(metrics.credits_total)} total`}
          description='Daily credit consumption for selected period'
        >
          <TimeSeriesChart
            data={creditsData}
            dataKey='credits'
            type='area'
            tooltipLabel='Credits'
            tooltipFormatter={creditTooltip}
          />
        </MetricCard>

        <MetricCard
          title='Credits by generation type'
          description='What consumed the credits in the selected period'
        >
          <CategoryBarChart
            data={typesData}
            categoryKey='label'
            valueKey='credits'
            tooltipLabel='Credits'
            tooltipFormatter={creditTooltip}
          />
        </MetricCard>
      </div>

      {/* Navigation Cards */}
      <div className='grid gap-4 md:grid-cols-2'>
        <Link to='/admin/organizations' replace>
          <Card className='hover:bg-muted/50 cursor-pointer transition-colors'>
            <CardHeader className='flex flex-row items-center justify-between space-y-0 pb-2'>
              <CardTitle className='flex items-center gap-2 text-sm font-medium'>
                <Building2 className='text-primary' />
                Organizations
              </CardTitle>
              <ChevronRight className='text-muted-foreground h-4 w-4' />
            </CardHeader>
            <CardContent>
              <div className='font-heading text-4xl'>
                {organizations.length}
              </div>
            </CardContent>
          </Card>
        </Link>

        <Link to='/admin/billing' replace>
          <Card className='hover:bg-muted/50 cursor-pointer transition-colors'>
            <CardHeader className='flex flex-row items-center justify-between space-y-0 pb-2'>
              <CardTitle className='flex items-center gap-2 text-sm font-medium'>
                <CreditCard className='text-primary' />
                Billing Accounts
              </CardTitle>
              <ChevronRight className='text-muted-foreground h-4 w-4' />
            </CardHeader>
            <CardContent>
              <div className='font-heading text-4xl'>
                {billingAccounts.length}
              </div>
            </CardContent>
          </Card>
        </Link>
      </div>
    </div>
  )
}
